import {EScene} from '@/types/scene.types'
import {Button} from '@repo/shared/Customer/Coop/Button/Button'
import {ContentWidthLimiter} from '@repo/shared/Customer/Coop/ContentWidthLimiter/ContentWidthLimiter'
import {
  ESprintenTeam,
  SprintenTeamsLogoMap,
  SprintenTeamsNameMap,
} from '@repo/shared/Customer/Coop/SprintenTeams/SprintenTeams.types'
import {isEventGameCode} from '@repo/shared/Customer/Coop/utils/gameCodeTypes'
import {useTracking} from '@repo/shared/Tracking/Tracking.hooks'
import {getTrackingGameMode} from '@repo/shared/Tracking/Tracking.utils'
import {debugLogger} from '@repo/shared/utils/debugLogger'
import clsx from 'clsx'
import gsap from 'gsap'
import {useAtomValue} from 'jotai'
import {FC, useEffect, useMemo, useRef} from 'react'
import {TransitionStatus} from 'react-transition-state'
import {getStopwatchTime, getTimeDiffBetweenTimestamps} from '../Hud/Stopwatch/Stopwatch.utils'
import {useReset} from '../Reset/Reset.hooks'
import {
  endPositonsState,
  gameCodeState,
  gameModeState,
  timestampGameStartedState,
} from '../Store/atoms'
import FrameStore from '../Store/FrameStore'

interface IScoreboardPartyProps {
  transitionStatus: TransitionStatus
}

const fStore = new FrameStore()

const ScoreboardParty: FC<IScoreboardPartyProps> = ({transitionStatus}) => {
  const endPositions = useAtomValue(endPositonsState)
  const gameMode = useAtomValue(gameModeState)
  const gameStartedTimestamp = useAtomValue(timestampGameStartedState)
  const {resetAll} = useReset()
  const {trackEvent} = useTracking()
  const gameCode = useAtomValue(gameCodeState)
  const isEvent = !!gameCode && isEventGameCode(gameCode)

  const itemsRef = useRef<HTMLDivElement[]>([])

  const enterTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))
  const exitTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))

  const scoreboardList = useMemo(() => {
    const leaderboard = fStore.getLeaderboard()
    const endedPlayers = endPositions.sort((a, b) => a.position - b.position)

    const dnfPlayers =
      leaderboard?.leaderboardTeams.filter(
        player => !endedPlayers.find(endedPlayer => endedPlayer.slot === player.slot),
      ) ?? []

    const dnfPlayersWithEndTimestamp = dnfPlayers.map(player => {
      return {
        team: player.team,
        position: endedPlayers.length + 1,
        teamEndTimestamp: undefined,
      }
    })

    return [...endedPlayers, ...dnfPlayersWithEndTimestamp]
  }, [endPositions])

  /**
   * Enter and leave states
   */
  useEffect(() => {
    if (transitionStatus === 'entered') {
      debugLogger({
        message: 'Enter state',
        tag: 'Scoreboard [party]',
      })
      enterTimeline.current.play()
    } else if (transitionStatus === 'exiting') {
      debugLogger({
        message: 'Exit state',
        tag: 'Scoreboard [party]',
      })
      enterTimeline.current.pause()
      exitTimeline.current.play()
    }
  }, [transitionStatus])

  if (!scoreboardList.length) {
    return null
  }

  return (
    <>
      <div className="fixed inset-0 z-10 bg-[linear-gradient(to_bottom,#3e59e0,#160278)] opacity-70" />
      <ContentWidthLimiter id="scoreboard" classNameOuter="z-20" classNameInner="text-center">
        <div className="z-20 mb-[48px] mt-[10vh] text-center">
          <img className="mx-auto ~w-[200px]/[712px]" src="assets/logo.svg" />
        </div>

        <div className="mx-auto w-full max-w-[880px] rounded-[40px] bg-[rgba(7,17,68,0.72)] px-[40px] py-[44px] text-[56px] text-white shadow-[0_0_64px_0_rgba(255,255,255,0.24)]">
          {
            // endPositions
            //   .sort((a, b) => a.position - b.position)
            scoreboardList.map((team, index, _currentArray) => {
              const teamIndex = index + 1

              return (
                <div
                  ref={ref => {
                    if (ref) itemsRef.current[index] = ref
                  }}
                  key={teamIndex}
                  className={clsx(
                    'scoreboard-item flex gap-8',
                    teamIndex !== scoreboardList.length &&
                      'mb-[24px] border-b-2 border-[#ffffff3d] pb-[24px]',
                  )}
                >
                  <div className={clsx('flex h-[60px] flex-1 items-center gap-[24px] uppercase')}>
                    {team.teamEndTimestamp ? (
                      <div className="position-index flex w-[48px]">{team.position}.</div>
                    ) : (
                      <div className="flex w-[48px]"></div>
                    )}
                    <div
                      className={clsx(
                        'size-[64px] rounded-[16px]',
                        team.team === ESprintenTeam.Anglamark && 'bg-team-anglamark',
                        team.team === ESprintenTeam.CoopMega && 'bg-team-coop_mega',
                        team.team === ESprintenTeam.CoopPrix && 'bg-team-coop_prix',
                        team.team === ESprintenTeam.Extra && 'bg-team-extra',
                        team.team === ESprintenTeam.OBS && 'bg-team-obs',
                      )}
                    >
                      <img src={SprintenTeamsLogoMap[team.team]} alt="" />
                    </div>
                    <span className="position-name">{SprintenTeamsNameMap[team.team]}</span>
                  </div>

                  {/* Teams with finish times */}
                  {team.teamEndTimestamp && (
                    <div className="position-score flex h-[60px] items-center justify-center font-normal text-white">
                      {team.position === 1 && gameStartedTimestamp && team.teamEndTimestamp
                        ? getStopwatchTime(gameStartedTimestamp, team.teamEndTimestamp)
                        : ''}

                      {team.position !== 1 &&
                        team.teamEndTimestamp &&
                        '+ ' +
                          getTimeDiffBetweenTimestamps(
                            _currentArray[0]?.teamEndTimestamp ?? 0,
                            team.teamEndTimestamp,
                          )}
                    </div>
                  )}

                  {/* Teams that DNF */}
                  {!team.teamEndTimestamp && (
                    <div className="flex h-[60px] items-center justify-center font-normal text-white text-opacity-40">
                      DNF
                    </div>
                  )}
                </div>
              )
            })
          }
        </div>
        {!isEvent && (
          <div className="fixed right-[24px] top-[24px] flex flex-col gap-4">
            <Button
              id="play-again-btn"
              onClick={() => {
                trackEvent({
                  domain: EScene.SCOREBOARD,
                  trigger: 'click',
                  source: 'play_again_button',
                  meta: {
                    gameMode: getTrackingGameMode(gameMode),
                  },
                })

                resetAll()
              }}
              color="purple"
              size="small"
              fullWidth
              ignoreMinWidth
            >
              Spill igjen
            </Button>
            <Button
              id="redirect-to-coop"
              onClick={() => {
                trackEvent({
                  domain: EScene.SCOREBOARD,
                  trigger: 'click',
                  source: 'redirect_to_coop',
                  meta: {
                    gameMode: getTrackingGameMode(gameMode),
                  },
                })
                window.open('https://www.coop.no/')
              }}
              color="purple"
              size="small"
              fullWidth
              ignoreMinWidth
            >
              Gå til Coop.no
            </Button>
          </div>
        )}
      </ContentWidthLimiter>
    </>
  )
}

export default ScoreboardParty
